/**
 * lazyWithReload.ts — survive a deploy that happens while a tab is open.
 *
 * ── WHY (John, 2026-09-27) ──────────────────────────────────────────────────
 * Clicking a link in the admin dashboard (and the Control Center) sometimes
 * showed "Unexpected Application Error! Failed to fetch dynamically imported
 * module: …/admin/assets/IndustryKnowledge-DUIlboWn.js", fixed only by a manual
 * refresh.
 *
 * Every page is its own code-split chunk with a content hash in its name. A
 * build empties the output folder, so a deploy DELETES the previous chunks. A tab
 * opened before the deploy still holds the old index, which points at the old
 * names. Measured that day: the missing chunk URL answered 200 text/html — the
 * SPA rewrite serves index.html for any unknown path — so the browser rejected
 * it as a module and React Router showed its raw error screen.
 *
 * The fix is the one a human was doing by hand: when a chunk fails to load,
 * reload the page, which fetches the new index and the new chunk names.
 *
 * ⚠️ ONCE, NOT FOREVER. If the reload does not cure it (a real outage, or a
 * cached stale index), reloading again would loop. So a reload is allowed at
 * most once per RELOAD_WINDOW_MS; after that the error surfaces normally. And if
 * sessionStorage cannot be used we do NOT reload at all — without the guard
 * there is no way to know we have not just done it.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

const RELOAD_KEY = 'bas:chunk-reload-at'
export const RELOAD_WINDOW_MS = 30_000

/** PURE — is this the error a browser throws when a code-split chunk is gone? */
export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '')
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i.test(msg)
}

/**
 * Reload the page to pick up the new version, unless we already did so in the
 * last RELOAD_WINDOW_MS. Returns true when a reload was started.
 */
export function reloadOnceForNewVersion(now: number = Date.now()): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0)
    if (now - last < RELOAD_WINDOW_MS) return false
    window.sessionStorage.setItem(RELOAD_KEY, String(now))
  } catch {
    return false
  }
  window.location.reload()
  return true
}

/**
 * `React.lazy` that reloads the page once when the chunk has been removed by a
 * deploy. While the reload is in flight it never resolves, so the Suspense
 * fallback ("Loading…") shows instead of an error screen.
 */
export function lazyWithReload<T extends ComponentType<any>>( // eslint-disable-line @typescript-eslint/no-explicit-any
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((err: unknown) => {
      if (isChunkLoadError(err) && reloadOnceForNewVersion()) {
        return new Promise<{ default: T }>(() => {})
      }
      throw err
    }),
  )
}

/**
 * Vite fires `vite:preloadError` when a chunk's preloaded dependency (JS or CSS)
 * fails. Install once at startup so that path reloads too.
 */
export function installChunkReloadHandler(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadOnceForNewVersion()) event.preventDefault()
  })
}
